import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Loader2, Paperclip, RefreshCw, Send } from 'lucide-react';
import {
  downloadKlachtBijlage,
  getAdminKlacht,
  getAdminKlachten,
  handelAdminKlachtAf,
  werkAdminKlachtBij,
  type KlachtStatus,
} from '../services/klachtApi';

const STATUS_KLEUR: Record<KlachtStatus, string> = {
  ontvangen: 'bg-amber-50 text-amber-700 border-amber-200',
  in_behandeling: 'bg-sky-50 text-sky-700 border-sky-200',
  afgehandeld: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

/** uiterste_reactiedatum komt als kalenderdatum (YYYY-MM-DD); die als lokale datum lezen, niet als UTC. */
const naarDatum = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`) : new Date(iso));
const datum = (iso: string) => naarDatum(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
const dagStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** Behandeling van platformklachten (klachtenreglement art. 3): termijnbewaking, notitie en uitspraak. */
export const AdminKlachtenPage: React.FC = () => {
  const { t } = useTranslation('klachten');
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<'open' | 'alles'>('open');
  const [gekozen, setGekozen] = useState<string | null>(null);
  const [notitie, setNotitie] = useState('');
  const [uitspraak, setUitspraak] = useState('');

  const lijst = useQuery({
    queryKey: ['admin-klachten', scope],
    queryFn: async () => {
      const alles = await getAdminKlachten();
      return scope === 'open' ? alles.filter((k) => k.status !== 'afgehandeld') : alles;
    },
  });
  const detail = useQuery({ queryKey: ['admin-klacht', gekozen], queryFn: () => getAdminKlacht(gekozen!), enabled: !!gekozen });

  useEffect(() => {
    setNotitie(detail.data?.interne_notitie ?? '');
    setUitspraak(detail.data?.uitspraak ?? '');
  }, [detail.data?.id, detail.data?.interne_notitie, detail.data?.uitspraak]);

  const ververs = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-klachten'] });
    queryClient.invalidateQueries({ queryKey: ['admin-klacht', gekozen] });
  };
  const foutmelding = (err: { response?: { data?: { message?: string } } }) => toast.error(err?.response?.data?.message || 'Er is een fout opgetreden');

  const bijwerken = useMutation({
    mutationFn: (invoer: { status?: 'in_behandeling'; interne_notitie?: string }) => werkAdminKlachtBij(gekozen!, invoer),
    onSuccess: () => {
      toast.success(t('admin.opgeslagen'));
      ververs();
    },
    onError: foutmelding,
  });
  const afhandelen = useMutation({
    mutationFn: () => handelAdminKlachtAf(gekozen!, uitspraak.trim()),
    onSuccess: () => {
      toast.success(t('admin.afgehandeld'));
      ververs();
    },
    onError: foutmelding,
  });

  const vandaag = dagStart(new Date());
  const termijnLabel = (iso: string, status: KlachtStatus) => {
    if (status === 'afgehandeld') return <span className="text-gray-500">{datum(iso)}</span>;
    const termijn = dagStart(naarDatum(iso));
    if (termijn < vandaag) return <span className="font-semibold text-red-600">{datum(iso)} · {t('admin.verlopen')}</span>;
    if (termijn === vandaag) return <span className="font-semibold text-amber-600">{t('admin.vandaag')}</span>;
    return <span className="text-gray-700">{datum(iso)}</span>;
  };

  const k = detail.data;

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          {(['open', 'alles'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              className={`rounded-full border px-3 py-1 text-sm font-medium ${scope === s ? 'border-navy-900 bg-navy-900 text-white' : 'border-gray-300 bg-white text-gray-700'}`}
            >
              {s === 'open' ? t('admin.filterOpen') : t('admin.filterAlles')}
            </button>
          ))}
          <button type="button" onClick={() => lijst.refetch()} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" aria-label="Vernieuwen">
            <RefreshCw className={`h-4 w-4 ${lijst.isRefetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        {/* Lijst */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          {lijst.isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : (lijst.data ?? []).length === 0 ? (
            <p className="p-6 text-sm text-gray-500">{t('admin.leeg')}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {(lijst.data ?? []).map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setGekozen(r.id)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 ${gekozen === r.id ? 'bg-navy-50' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-mono text-sm font-semibold text-gray-900">{r.ticket_nummer}</p>
                        <p className="truncate text-xs text-gray-500">
                          {t(`type.${r.gebruiker_type}`)}
                          {r.organisatie_naam ? ` · ${r.organisatie_naam}` : ''}
                        </p>
                        <p className="text-xs text-gray-500">{t(`categorie.${r.categorie}`)}</p>
                      </div>
                      <div className="flex flex-shrink-0 flex-col items-end gap-1 text-xs">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${STATUS_KLEUR[r.status]}`}>{t(`status.${r.status}`)}</span>
                        {termijnLabel(r.uiterste_reactiedatum, r.status)}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          {!gekozen ? (
            <p className="text-sm text-gray-500">{t('admin.kies')}</p>
          ) : detail.isLoading || !k ? (
            <div className="flex min-h-[200px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : (
            <div className="space-y-5 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-mono text-base font-semibold text-gray-900">{k.ticket_nummer}</p>
                  <p className="text-xs text-gray-500">
                    {t(`type.${k.gebruiker_type}`)} · {t(`categorie.${k.categorie}`)} · {datum(k.created_at)}
                  </p>
                </div>
                <div className="text-right text-xs">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${STATUS_KLEUR[k.status]}`}>{t(`status.${k.status}`)}</span>
                  <p className="mt-1">{t('admin.kolomTermijn')}: {termijnLabel(k.uiterste_reactiedatum, k.status)}</p>
                </div>
              </div>

              <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[10rem_1fr]">
                <dt className="text-gray-500">{t('admin.contact')}</dt>
                <dd className="text-gray-900">
                  {k.contact_naam}
                  {k.organisatie_naam ? ` (${k.organisatie_naam})` : ''}
                  <br />
                  <a href={`mailto:${k.email}`} className="text-navy-900 underline">{k.email}</a> · {k.telefoon}
                </dd>
                <dt className="text-gray-500">{t('admin.referentie')}</dt>
                <dd className="break-all font-mono text-gray-900">{k.referentie}</dd>
                {k.academy_boeking_id && (
                  <>
                    <dt className="text-gray-500">{t('admin.boeking')}</dt>
                    <dd className="text-gray-900">
                      {k.boeking_titel} · {k.boeking_status} · €{Number(k.boeking_bedrag ?? 0).toFixed(2)} (budget €{Number(k.boeking_wallet_deel ?? 0).toFixed(2)})
                    </dd>
                  </>
                )}
                {k.stripe_referentie && (
                  <>
                    <dt className="text-gray-500">{t('admin.stripe')}</dt>
                    <dd className="break-all font-mono text-gray-900">{k.stripe_referentie}</dd>
                  </>
                )}
                {k.bijlage_naam && (
                  <>
                    <dt className="text-gray-500">{t('admin.bijlage')}</dt>
                    <dd>
                      <button type="button" onClick={() => downloadKlachtBijlage(k.id, k.bijlage_naam!).catch(() => toast.error('Download mislukt'))} className="inline-flex items-center gap-1 text-navy-900 underline">
                        <Paperclip className="h-3.5 w-3.5" />
                        {k.bijlage_naam}
                      </button>
                    </dd>
                  </>
                )}
              </dl>

              <div>
                <p className="mb-1 font-semibold text-gray-900">{t('admin.omschrijving')}</p>
                <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-gray-800">{k.omschrijving}</p>
              </div>
              <div>
                <p className="mb-1 font-semibold text-gray-900">{t('admin.oplossing')}</p>
                <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-gray-800">{k.gewenste_oplossing}</p>
              </div>
              <p className="text-xs text-gray-400">{t('admin.akkoord', { versie: k.reglement_versie, datum: datum(k.akkoord_op) })}</p>

              {k.status !== 'afgehandeld' ? (
                <>
                  <div>
                    <label htmlFor="klacht-notitie" className="mb-1 block font-semibold text-gray-900">{t('admin.notitie')}</label>
                    <textarea id="klacht-notitie" value={notitie} onChange={(e) => setNotitie(e.target.value)} className="min-h-[80px] w-full rounded-lg border border-gray-300 px-3 py-2" maxLength={10000} />
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button type="button" onClick={() => bijwerken.mutate({ interne_notitie: notitie })} disabled={bijwerken.isPending} className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        {t('admin.notitieOpslaan')}
                      </button>
                      {k.status === 'ontvangen' && (
                        <button type="button" onClick={() => bijwerken.mutate({ status: 'in_behandeling' })} disabled={bijwerken.isPending} className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-1.5 font-medium text-sky-800 hover:bg-sky-100 disabled:opacity-50">
                          {t('admin.inBehandeling')}
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="klacht-uitspraak" className="mb-1 block font-semibold text-gray-900">{t('admin.uitspraak')}</label>
                    <textarea id="klacht-uitspraak" value={uitspraak} onChange={(e) => setUitspraak(e.target.value)} placeholder={t('admin.uitspraakPlaceholder')} className="min-h-[140px] w-full rounded-lg border border-gray-300 px-3 py-2" maxLength={20000} />
                    <button
                      type="button"
                      onClick={() => window.confirm(t('admin.afhandelenBevestig')) && afhandelen.mutate()}
                      disabled={uitspraak.trim().length < 20 || afhandelen.isPending}
                      className="mt-2 inline-flex items-center gap-2 rounded-lg bg-navy-900 px-4 py-2 font-semibold text-white disabled:opacity-50"
                    >
                      {afhandelen.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {t('admin.afhandelen')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {k.interne_notitie && (
                    <div>
                      <p className="mb-1 font-semibold text-gray-900">{t('admin.notitie')}</p>
                      <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-gray-800">{k.interne_notitie}</p>
                    </div>
                  )}
                  <div>
                    <p className="mb-1 font-semibold text-gray-900">{t('admin.uitspraak')}</p>
                    <p className="whitespace-pre-wrap rounded-lg border-l-4 border-navy-900 bg-gray-50 p-3 text-gray-800">{k.uitspraak}</p>
                    {k.afgehandeld_op && <p className="mt-1 text-xs text-gray-500">{t('mijn.afgehandeld', { datum: datum(k.afgehandeld_op) })}</p>}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminKlachtenPage;
